import axios from "axios";

const BASE_URL = process.env.REACT_APP_ROCKETCHAT_BASE_URL || "";

/**
 * Initialize a new chat session
 * @param {Object} userInfo - User information object
 * @param {string} userInfo.customer_name - Customer name
 * @param {string} userInfo.customer_email - Customer email
 * @param {string} userInfo.channel_id - Channel ID
 * @returns {Promise<Object>} Response object with success status and session data
 */
export const initializeChatSession = async (userInfo) => {
  try {
    const response = await axios.post(
      `${BASE_URL}api/v1/taskboard/sessions`,
      userInfo,
      {
        headers: { "Content-Type": "application/json" },
      }
    );
    
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("Initialize chat session error:", error);
    return {
      success: false,
      error: error.response?.data?.message || "Failed to initialize chat session",
      data: null,
    };
  }
};

/**
 * Send a message in a chat session
 * @param {string} sessionId - Chat session ID
 * @param {Object} messageData - Message data object
 * @param {string} messageData.message - Message text
 * @returns {Promise<Object>} Response object with success status and message data
 */
export const sendMessage = async (sessionId, messageData) => {
  try {
    const response = await axios.post(
      `${BASE_URL}api/v1/sessions/${sessionId}/messages`,
      messageData,
      {
        headers: { "Content-Type": "application/json" },
      }
    );
    
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("Send message error:", error);
    return {
      success: false,
      error: error.response?.data?.message || "Failed to send message",
      data: null,
    };
  }
};

/**
 * Get chat messages for a specific session
 * @param {string} sessionId - Chat session ID
 * @returns {Promise<Object>} Response object with success status and messages data
 */
export const getChatMessages = async (sessionId) => {
  try {
    const response = await axios.get(
      `${BASE_URL}api/v1/sessions/${sessionId}/messages`,
      {
        headers: { "Content-Type": "application/json" },
      }
    );
    
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    localStorage.removeItem('chat_session_id');
    localStorage.removeItem('chat_session_data');
    console.error("Get chat messages error:", error);
    return {
      success: false,
      error: error.response?.data?.message || "Failed to get chat messages",
      data: null,
    };
  }
};

/**
 * Close a chat session
 * @param {string} sessionId - Chat session ID
 * @returns {Promise<Object>} Response object with success status
 */
export const closeChatSession = async (sessionId) => {
  try {
    const response = await axios.post(
      `${BASE_URL}chat/${sessionId}/close`,
      {},
      {
        headers: { "Content-Type": "application/json" },
      }
    );
    
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("Close chat session error:", error);
    return {
      success: false,
      error: error.response?.data?.message || "Failed to close chat session",
      data: null,
    };
  }
};
